<script lang="ts">
  import { MapPin, Clock, Phone, Mail, Navigation, ArrowUpRight } from '@lucide/svelte';
  import { ORTHOHOUSE_URL } from '$lib/utils/orthohouse';
  import { onMount } from 'svelte';
  import { m } from '$lib/paraglide/messages';
  import { getLocale } from '$lib/paraglide/runtime';
  import { isViewTransitioning } from '$lib/stores/viewTransition.svelte';

  // Strip Greek diacritics for proper uppercase display (Safari iOS bug workaround)
  function toUppercaseGreek(str: string): string {
    return str
      .toUpperCase()
      .replace(/Ά/g, 'Α')
      .replace(/Έ/g, 'Ε')
      .replace(/Ή/g, 'Η')
      .replace(/Ί/g, 'Ι')
      .replace(/Ϊ́/g, 'Ϊ')
      .replace(/Ό/g, 'Ο')
      .replace(/Ύ/g, 'Υ')
      .replace(/Ϋ́/g, 'Ϋ')
      .replace(/Ώ/g, 'Ω');
  }

  // Skip entry animation during SPA navigation (view transition handles it)
  let visible = $state(isViewTransitioning);
  let currentTime = $state(new Date());

  onMount(() => {
    if (!visible) {
      setTimeout(() => {
        visible = true;
      }, 50);
    }

    // Update time every minute
    const interval = setInterval(() => {
      currentTime = new Date();
    }, 60000);

    return () => clearInterval(interval);
  });

  const business = {
    name: "O.H. OrthoHouse Ltd",
    address: "27B, 25th March Street",
    city: "Engomi, 2408 Nicosia",
    country: "Cyprus",
    phone: "+357 22 66 06 88",
    email: "info@orthohouse.com.cy",
    coords: { lat: 35.1648933, lng: 33.3309487 },
    placeId: "ChIJv7X2qQQaHhQRT9DfdSnY9a8"
  };

  // Hours in 24h format for easy comparison
  const schedule: Record<number, { open: string; close: string } | null> = {
    0: null, // Sunday - closed
    1: { open: "09:00", close: "19:00" },
    2: { open: "09:00", close: "19:00" },
    3: { open: "09:00", close: "19:00" },
    4: { open: "09:00", close: "19:00" },
    5: { open: "09:00", close: "19:00" },
    6: { open: "09:00", close: "14:00" },
  };

  const dayKeys = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;

  function getDayName(dayIndex: number): string {
    const names: Record<typeof dayKeys[number], () => string> = {
      sunday: m.sunday,
      monday: m.monday,
      tuesday: m.tuesday,
      wednesday: m.wednesday,
      thursday: m.thursday,
      friday: m.friday,
      saturday: m.saturday,
    };
    return names[dayKeys[dayIndex]]();
  }

  // Get "at [time]" phrase for each locale
  function getTimeAtPhrase(time: string): string {
    const locale = getLocale();
    switch (locale) {
      case 'el': return `στις ${time}`;
      case 'ru': return `в ${time}`;
      case 'he': return `ב-${time}`;
      default: return `at ${time}`;
    }
  }

  // Get day with preposition/article and time for each locale
  function getNextOpenTime(dayIndex: number, time: string): string {
    const locale = getLocale();

    if (locale === 'el') {
      const dayName = getDayName(dayIndex);
      // Saturday uses "το", others use "την"
      const article = dayIndex === 6 ? 'το' : 'την';
      return `${article} ${dayName} στις ${time}`;
    }

    if (locale === 'ru') {
      // Russian: accusative case (lowercase) with в/во preposition
      const ruDaysAccusative: Record<number, string> = {
        0: 'в воскресенье',
        1: 'в понедельник',
        2: 'во вторник',      // "во" before consonant cluster
        3: 'в среду',         // accusative ending
        4: 'в четверг',
        5: 'в пятницу',       // accusative ending
        6: 'в субботу'        // accusative ending
      };
      return `${ruDaysAccusative[dayIndex]} в ${time}`;
    }

    if (locale === 'he') {
      const dayName = getDayName(dayIndex);
      // Hebrew: ב prefix turns יום into ביום (on day)
      return `ב${dayName} ב-${time}`;
    }

    // English: "on Monday at 09:00"
    const dayName = getDayName(dayIndex);
    return `on ${dayName} at ${time}`;
  }

  function getHoursDisplay(dayIndex: number): string {
    const hours = schedule[dayIndex];
    if (!hours) return m.closed();
    return `${hours.open} – ${hours.close}`;
  }

  function parseTime(timeStr: string): number {
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
  }

  function getCurrentMinutes(date: Date): number {
    return date.getHours() * 60 + date.getMinutes();
  }

  let storeStatus = $derived.by(() => {
    const today = currentTime.getDay();
    const todaySchedule = schedule[today];
    const nowMinutes = getCurrentMinutes(currentTime);

    if (!todaySchedule) {
      // Closed today, find next open day
      for (let i = 1; i <= 7; i++) {
        const nextDay = (today + i) % 7;
        const nextSchedule = schedule[nextDay];
        if (nextSchedule) {
          return {
            isOpen: false,
            message: m.opens_at({ time: getNextOpenTime(nextDay, nextSchedule.open) })
          };
        }
      }
      return { isOpen: false, message: m.closed_now() };
    }

    const openMinutes = parseTime(todaySchedule.open);
    const closeMinutes = parseTime(todaySchedule.close);

    if (nowMinutes < openMinutes) {
      return {
        isOpen: false,
        message: m.opens_at({ time: getTimeAtPhrase(todaySchedule.open) })
      };
    } else if (nowMinutes < closeMinutes) {
      return {
        isOpen: true,
        message: m.closes_at({ time: todaySchedule.close })
      };
    } else {
      // Closed for today, find next open
      for (let i = 1; i <= 7; i++) {
        const nextDay = (today + i) % 7;
        const nextSchedule = schedule[nextDay];
        if (nextSchedule) {
          return {
            isOpen: false,
            message: m.opens_at({ time: getNextOpenTime(nextDay, nextSchedule.open) })
          };
        }
      }
      return { isOpen: false, message: m.closed_now() };
    }
  });

  let todayIndex = $derived(currentTime.getDay());

  const directions = {
    google: `https://www.google.com/maps/dir/?api=1&destination=${business.coords.lat},${business.coords.lng}&destination_place_id=${business.placeId}`,
    waze: `https://waze.com/ul?ll=${business.coords.lat},${business.coords.lng}&navigate=yes`,
    twoGis: "https://2gis.com.cy/cyprus/firm/70000001028916464"
  };

  const mapsEmbedUrl = `https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1630.2!2d${business.coords.lng}!3d${business.coords.lat}!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14de1a04a9f6b5bf%3A0xaf58fe2975df504f!2sO.H.OrthoHouse%20Ltd!5e0!3m2!1sen!2scy!4v1705500000000`;

  // Ordered days starting from Monday (more natural for display)
  const displayOrder = [1, 2, 3, 4, 5, 6, 0]; // Mon-Sun
</script>

<svelte:head>
  <title>{m.find_us()} | Healthy Shoes Cyprus - Engomi, Nicosia</title>
  <meta name="description" content="Visit Healthy Shoes Cyprus in Engomi, Nicosia. Open Mon-Fri 9:00-19:00, Sat 9:00-14:00. Official kybun partner. Get directions and contact info." />

  <!-- Open Graph / Facebook -->
  <meta property="og:type" content="website" />
  <meta property="og:title" content="{m.find_us()} | Healthy Shoes Cyprus" />
  <meta property="og:description" content="Visit us in Engomi, Nicosia. Official kybun partner in Cyprus. Experience pain-free walking today!" />
  <meta property="og:image" content="https://healthyshoes.com.cy/kybun1.jpg" />

  <!-- Twitter Card -->
  <meta name="twitter:card" content="summary_large_image" />
  <meta name="twitter:title" content="{m.find_us()} | Healthy Shoes Cyprus" />
  <meta name="twitter:description" content="Visit us in Engomi, Nicosia. Official kybun partner in Cyprus. Experience pain-free walking today!" />
  <meta name="twitter:image" content="https://healthyshoes.com.cy/kybun1.jpg" />

  <!-- Local Business Schema -->
  {@html `<script type="application/ld+json">
    {
      "@context": "https://schema.org",
      "@type": "ShoeStore",
      "name": "Healthy Shoes Cyprus - O.H. OrthoHouse Ltd",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "27B, 25th March Street",
        "addressLocality": "Engomi",
        "postalCode": "2408",
        "addressRegion": "Nicosia",
        "addressCountry": "CY"
      },
      "telephone": "+357 22 66 06 88",
      "email": "info@orthohouse.com.cy",
      "openingHoursSpecification": [
        {"@type": "OpeningHoursSpecification", "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday"], "opens": "09:00", "closes": "19:00"},
        {"@type": "OpeningHoursSpecification", "dayOfWeek": "Saturday", "opens": "09:00", "closes": "14:00"}
      ],
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 35.1648933,
        "longitude": 33.3309487
      }
    }
  </script>`}
</svelte:head>

<div class="find-us-page bg-gray-50 dark:bg-primary">
    <div class="content-wrapper transition-opacity duration-300 {visible ? 'opacity-100' : 'opacity-0'}">
      <!-- Hero Section -->
      <header class="hero">
        <div class="hero-content">
          <h1 class="title text-primary dark:text-white">{m.find_us()}</h1>
          <p class="subtitle text-gray-600 dark:text-white/70">{m.find_us_subtitle()}</p>
          <!-- The store on the map is OrthoHouse's: say so, and point to the parent shop -->
          <p class="parent-note text-gray-500 dark:text-white/60" dir="auto">
            {m.orthohouse_about()}
            <a
              href={ORTHOHOUSE_URL}
              class="parent-link text-primary decoration-primary/30 hover:decoration-primary dark:text-white dark:decoration-white/40 dark:hover:decoration-white"
            >{m.orthohouse_visit()}<span class="sr-only"> (orthohouse.com.cy)</span><ArrowUpRight size={14} color="currentColor" class="shrink-0 rtl:-scale-x-100" aria-hidden="true" /></a>
          </p>
        </div>
      </header>

      <!-- Main Content -->
      <div class="main-grid">
        <!-- Map Section -->
        <div class="map-container">
          <iframe
            title="Store Location"
            src={mapsEmbedUrl}
            width="100%"
            height="100%"
            style="border:0;"
            allowfullscreen
            loading="lazy"
            referrerpolicy="no-referrer-when-downgrade"
          ></iframe>
        </div>

        <!-- Info Section -->
        <div class="info-section">
          <!-- Address Card -->
          <div class="info-card bg-white dark:bg-white/10">
            <div class="icon-wrapper">
              <MapPin color="white" />
            </div>
            <div class="info-content">
              <h2 class="text-gray-500 dark:text-white/60">{toUppercaseGreek(m.address())}</h2>
              <p class="text-gray-800 dark:text-white">
                <bdi>{m.address_street()}</bdi><br />
                <bdi>{m.address_city()}</bdi><br />
                <bdi>{m.address_country()}</bdi>
              </p>
            </div>
          </div>

          <!-- Directions Card -->
          <div class="info-card directions-card bg-white dark:bg-white/10">
            <div class="info-content">
              <h2 class="text-gray-500 dark:text-white/60">{toUppercaseGreek(m.get_directions())}</h2>
              <div class="directions-buttons">
                <a
                  href={directions.google}
                  target="_blank"
                  rel="noopener noreferrer"
                  class="direction-btn google"
                >
                  <svg class="btn-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                  </svg>
                  Google Maps
                </a>
                <a
                  href={directions.waze}
                  target="_blank"
                  rel="noopener noreferrer"
                  class="direction-btn waze"
                >
                  <!-- Waze icon from Simple Icons -->
                  <svg class="btn-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M13.218 0C9.915 0 6.835 1.49 4.723 4.148c-1.515 1.913-2.31 4.272-2.31 6.706v1.739c0 .894-.62 1.738-1.862 1.813-.298.025-.547.224-.547.522-.05.82.82 2.31 2.012 3.502.82.844 1.788 1.515 2.832 2.036a3 3 0 0 0 2.955 3.528 2.966 2.966 0 0 0 2.931-2.385h2.509c.323 1.689 2.086 2.856 3.974 2.21 1.64-.546 2.36-2.409 1.763-3.924a12.84 12.84 0 0 0 1.838-1.465 10.73 10.73 0 0 0 3.18-7.65c0-2.882-1.118-5.589-3.155-7.625A10.899 10.899 0 0 0 13.218 0zm0 1.217c2.558 0 4.967.994 6.78 2.807a9.525 9.525 0 0 1 2.807 6.78 9.526 9.526 0 0 1-2.807 6.78 9.647 9.647 0 0 1-6.78 2.807h-2.46a3.008 3.008 0 0 0-2.93-2.41 3.03 3.03 0 0 0-2.534 1.367v.024a8.945 8.945 0 0 1-2.41-1.788c-.844-.844-1.316-1.614-1.515-2.11a2.858 2.858 0 0 0 1.441-.846 2.959 2.959 0 0 0 .795-2.036v-1.789c0-2.11.696-4.197 2.012-5.861 1.863-2.385 4.62-3.726 7.6-3.726zm-2.41 5.986a1.192 1.192 0 0 0-1.191 1.192 1.192 1.192 0 0 0 1.192 1.193 1.192 1.192 0 0 0 1.191-1.193 1.192 1.192 0 0 0-1.192-1.192zm7.204 0a1.192 1.192 0 0 0-1.192 1.192 1.192 1.192 0 0 0 1.192 1.193 1.192 1.192 0 0 0 1.192-1.193 1.192 1.192 0 0 0-1.192-1.192zm-7.377 4.769a.596.596 0 0 0-.546.845 4.813 4.813 0 0 0 4.346 2.757 4.77 4.77 0 0 0 4.347-2.757.596.596 0 0 0-.547-.845h-.025a.561.561 0 0 0-.521.348 3.59 3.59 0 0 1-3.254 2.061 3.591 3.591 0 0 1-3.254-2.061.64.64 0 0 0-.546-.348z"/>
                  </svg>
                  Waze
                </a>
                <a
                  href={directions.twoGis}
                  target="_blank"
                  rel="noopener noreferrer"
                  class="direction-btn twogis"
                >
                  <!-- 2GIS official icon -->
                  <svg class="btn-icon" viewBox="-6 0 14932 15479" fill="currentColor">
                    <path fill-rule="evenodd" d="M11081.41 9218.29c-2695.72,13.7 -3131.78,1697.02 -3277.11,3120.34l-66.06 629.52 -541.83 0 -66.06 -629.52c-145.33,-1423.32 -594.61,-3106.65 -3197.84,-3120.34 -436.06,-930.68 -621.06,-1683.39 -621.06,-2531.9 0,-2121.25 1678.22,-4105.74 4162.45,-4105.74 2484.28,0 4136.06,1970.73 4136.06,4119.49 0.01,834.76 -79.27,1587.47 -528.55,2518.15l0 0zm-3633.89 -9210.56c-4096.39,0 -7452.84,3476.21 -7452.84,7732.41 0,4270.01 3356.45,7746.16 7452.84,7746.16 4136,0 7479.23,-3476.15 7479.23,-7746.16 0,-4256.19 -3343.23,-7732.41 -7479.23,-7732.41z"/>
                  </svg>
                  2GIS
                </a>
              </div>
            </div>
          </div>

          <!-- Hours Card -->
          <div class="info-card hours-card bg-white dark:bg-white/10">
            <div class="icon-wrapper">
              <Clock color="white" />
            </div>
            <div class="info-content">
              <div class="hours-header">
                <h2 class="text-gray-500 dark:text-white/60">{toUppercaseGreek(m.opening_hours())}</h2>
                <span class="status-badge" class:open={storeStatus.isOpen} class:closed={!storeStatus.isOpen}>
                  {storeStatus.isOpen ? m.open_now() : m.closed_now()}
                </span>
              </div>
              <p class="status-detail text-gray-500 dark:text-white/60">{storeStatus.message}</p>
              <div class="hours-grid">
                {#each displayOrder as dayIndex}
                  <span class="day text-gray-600 dark:text-white/60" class:today={dayIndex === todayIndex}>{getDayName(dayIndex)}</span>
                  <span class="time text-gray-800 dark:text-white/80" class:today={dayIndex === todayIndex} class:closed={!schedule[dayIndex]}><bdi>{getHoursDisplay(dayIndex)}</bdi></span>
                {/each}
              </div>
            </div>
          </div>

          <!-- Contact Cards Row -->
          <div class="contact-row">
            <a href="tel:{business.phone.replace(/\s/g, '')}" class="info-card contact-card bg-white dark:bg-white/10">
              <div class="icon-wrapper small">
                <Phone color="white" />
              </div>
              <div class="info-content">
                <h2 class="text-gray-500 dark:text-white/60">{toUppercaseGreek(m.call_us())}</h2>
                <span class="contact-value text-primary dark:text-white" dir="ltr">{business.phone}</span>
              </div>
            </a>

            <a href="mailto:{business.email}" class="info-card contact-card bg-white dark:bg-white/10">
              <div class="icon-wrapper small">
                <Mail color="white" />
              </div>
              <div class="info-content">
                <h2 class="text-gray-500 dark:text-white/60">{toUppercaseGreek(m.email())}</h2>
                <span class="contact-value text-primary dark:text-white">{business.email}</span>
              </div>
            </a>
          </div>
        </div>
      </div>
    </div>
</div>

<style>
  .find-us-page {
    min-height: calc(100dvh - 11rem);
  }

  .content-wrapper {
    max-width: var(--max-width-container);
    margin: 0 auto;
    padding: 0 1rem;
  }

  /* Hero */
  .hero {
    padding: 3rem 1rem;
    text-align: center;
  }

  .hero-content {
    max-width: 600px;
    margin: 0 auto;
  }

  .title {
    font-size: 2.5rem;
    font-weight: 700;
    margin-bottom: 0.5rem;
  }

  .subtitle {
    font-size: 1.125rem;
  }

  .parent-note {
    margin-top: 1rem;
    font-size: 0.875rem;
    line-height: 1.6;
    text-wrap: pretty;
  }

  .parent-link {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    font-weight: 500;
    white-space: nowrap;
    text-decoration-line: underline;
    text-underline-offset: 4px;
    border-radius: 2px;
    transition: text-decoration-color 150ms ease-out;
  }

  .parent-link:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: 2px;
  }

  /* Main Grid */
  .main-grid {
    display: grid;
    gap: 2rem;
    padding-bottom: 4rem;
  }

  @media (min-width: 960px) {
    .main-grid {
      grid-template-columns: 1.5fr 1fr;
      gap: 3rem;
      padding: 0 2rem 4rem;
    }

    .hero {
      padding: 4rem 1rem;
    }

    .title {
      font-size: 3rem;
    }
  }

  /* Map */
  .map-container {
    aspect-ratio: 16 / 10;
    border-radius: 1rem;
    overflow: hidden;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.1);
  }

  @media (min-width: 960px) {
    .map-container {
      aspect-ratio: auto;
      min-height: 560px;
    }
  }

  /* Info Section */
  .info-section {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  /* Info Cards */
  .info-card {
    display: flex;
    gap: 1rem;
    padding: 1.25rem;
    border-radius: 0.75rem;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
    transition: transform 0.2s ease, box-shadow 0.2s ease;
  }

  .info-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  }

  .icon-wrapper {
    flex-shrink: 0;
    width: 2.75rem;
    height: 2.75rem;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--color-primary);
    border-radius: 0.5rem;
  }

  .icon-wrapper.small {
    width: 2.25rem;
    height: 2.25rem;
  }

  .info-content {
    flex: 1;
    min-width: 0;
  }

  .info-content h2 {
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 0.05em;
    margin-bottom: 0.25rem;
  }

  .info-content p {
    font-size: 0.9375rem;
    line-height: 1.5;
  }

  /* Hours Card */
  .hours-header {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 0.25rem;
  }

  .hours-header h2 {
    margin-bottom: 0;
  }

  .status-badge {
    font-size: 0.6875rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    padding: 0.25rem 0.5rem;
    border-radius: 9999px;
  }

  .status-badge.open {
    background: #dcfce7;
    color: #166534;
  }

  .status-badge.closed {
    background: #fee2e2;
    color: #991b1b;
  }

  .status-detail {
    font-size: 0.8125rem;
    margin-bottom: 0.75rem;
  }

  /* Hours Grid */
  .hours-grid {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 0.125rem 1rem;
    font-size: 0.875rem;
  }

  .day.today {
    font-weight: 700;
  }

  .time {
    font-weight: 500;
    text-align: end;
  }

  .time.today {
    font-weight: 700;
  }

  .time.closed {
    opacity: 0.5;
  }

  /* Directions */
  .directions-card {
    flex-direction: column;
    gap: 0.5rem;
  }

  .directions-buttons {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
  }

  .direction-btn {
    flex: 1;
    min-width: 100px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    padding: 0.75rem 1rem;
    font-size: 0.875rem;
    font-weight: 500;
    color: white;
    border-radius: 0.5rem;
    transition: transform 0.15s ease, opacity 0.15s ease;
  }

  .direction-btn:hover {
    transform: scale(1.02);
    opacity: 0.9;
  }

  .direction-btn:active {
    transform: scale(0.98);
  }

  .btn-icon {
    width: 1.25rem;
    height: 1.25rem;
    flex-shrink: 0;
  }

  .direction-btn.google {
    background: linear-gradient(135deg, #4285f4, #34a853);
  }

  .direction-btn.waze {
    background: linear-gradient(135deg, #33ccff, #00c8ff);
  }

  .direction-btn.twogis {
    background: linear-gradient(135deg, #3db54a, #2e9c3f);
  }

  /* Contact Row */
  .contact-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
  }

  .contact-card {
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 0.75rem;
    padding: 1rem;
    text-decoration: none;
    cursor: pointer;
  }

  .contact-card .info-content {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .contact-value {
    font-size: 0.875rem;
    font-weight: 500;
    word-break: break-all;
  }

  .contact-card:hover .contact-value {
    text-decoration: underline;
  }

  @media (max-width: 400px) {
    .contact-row {
      grid-template-columns: 1fr;
    }

    .directions-buttons {
      flex-direction: column;
    }

    .direction-btn {
      min-width: unset;
    }
  }
</style>
